import { EvaContext } from "@springtree/eva-sdk-core-service";
import { MagicLinkHub } from "@springtree/eva-sdk-signalr";
import { LoginResponse, SvcLogin, SvcSendMagicLink } from "@springtree/eva-services-core";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { useAuthContext } from "~/contexts/auth";
import { useConfiguration } from "~/contexts/configuration";
import { useEvaService } from "~/hooks/use-eva-service";
import Logger from "~/services/logger/logger";
import { SignalRAction } from "~/shared/signalr-action.enum";

export function useMagicLinkSignalr({
  action,
  onLoginResponse,
}: {
  action: SignalRAction;
  onLoginResponse: (response: LoginResponse) => Promise<void>;
}) {
  const [hub, setHub] = useState<MagicLinkHub | null>(null);
  const router = useRouter();
  const { logout } = useAuthContext();

  const configuration = useConfiguration();
  const login = useEvaService(SvcLogin);
  const sendMagicLinkService = useEvaService(SvcSendMagicLink);

  async function sendMagicLink(email: string, orderId: string) {
    await logout();
    // 0. Send the magic link via email
    let response;
    try {
      response = await sendMagicLinkService({
        EmailAddress: email,
        RedirectUrl: `${window.location.origin}/${router.locale}/email-verified/?action=${action}&orderId=${orderId}`,
        OrderID: orderId,
      });
    } catch (error: any) {
      Logger.instance.error(error);
      throw error;
    }

    if (!response) {
      throw new Error(`Unexpected empty response from Core.SendMagicLink`);
    }

    // 1. Set the event handler
    hub?.setEventHandler("Confirmed", async (payload) => {
      const loginResponse = await login({
        Username: email,
        // @ts-expect-error - ApiKey instead of TemporaryToken
        AuthenticationToken: payload.ApiKey,
        Password: payload.TemporaryToken,
      });

      onLoginResponse(loginResponse!);
      hub.leave();
    });

    // 2. Actually join the hub and wait for the event
    await hub?.join({ subscriberName: "kiko-uc/useMagicLinkSignalr" });
    await hub?.subscribeToMagicLink({ RequestToken: response.RequestToken });
  }

  useEffect(() => {
    setHub(
      new MagicLinkHub({
        configuration: { Configuration: configuration.configuration as Record<string, any> },
        context: new EvaContext({
          endpointUrl: process.env.NEXT_PUBLIC_EVA_ENDPOINT_URL!,
          role: "CLOUD",
          organizationUnitId: configuration.organizationUnitId,
        }),
      })
    );
  }, [configuration.configuration, configuration.organizationUnitId]);

  return {
    hub,
    sendMagicLink,
  };
}
